/**
 * Network request particles system (green lines)
 * 
 * Handles event-driven green particles with trails for network requests
 * Particles travel along paths from camera to cortex (or vice versa)
 */

import type * as THREE_NS from 'three';
import { bus } from '../../../core/events.js';
import { generateConnectionPath, type PathGenerationOptions } from './connectionPaths.js';
import { loadSpriteTexture } from './spriteLoader.js';

export interface NetworkRequestLine {
  from: { x: number; y: number; z: number };
  to: { x: number; y: number; z: number };
  progress: number; // 0-1: line growth (dies immediately at 1, no shrink phase)
  duration: number;
  startTime: number;
  line: any; // Three.js line object
  sprite: any; // Sprite at the end of the line to make it look like a trail
  path: Array<{ x: number; y: number; z: number }>;
  colorHueOffset: number; // Unique hue offset per sprite for variation (limited to blue-green range)
  status?: 'normal' | 'error' | 'warning'; // Connection status for color coding
  isStartup?: boolean; // Flag to indicate this is from startup activation effect
}

export interface NetworkRequestParticlesConfig {
  NETWORK_LINE_DURATION_MIN: number;
  NETWORK_LINE_DURATION_MAX: number;
  MAX_NETWORK_LINES: number;
}

const DEFAULT_CONFIG: NetworkRequestParticlesConfig = {
  NETWORK_LINE_DURATION_MIN: 0.5, // Slower - 500ms
  NETWORK_LINE_DURATION_MAX: 1.0, // Slower - 1 second
  MAX_NETWORK_LINES: 15, // Limit to prevent excessive accumulation - reduced from 50
};

/**
 * Create network request particles system
 */
export function createNetworkRequestParticles(
  scene: THREE_NS.Scene,
  camera: THREE_NS.Camera,
  pathOptions: PathGenerationOptions,
  config: NetworkRequestParticlesConfig = DEFAULT_CONFIG
): {
  networkRequestLines: NetworkRequestLine[];
  createNetworkRequestLine: (isStartup?: boolean) => void;
  updateNetworkRequestLines: (currentTime: number) => void;
} {
  const { THREE } = pathOptions;
  const {
    BufferGeometry,
    Float32BufferAttribute,
    LineBasicMaterial,
    Line,
    Sprite,
    SpriteMaterial,
  } = THREE as any;

  const networkRequestLines: NetworkRequestLine[] = [];
  let startupLineIndex = 0; // Counter for ROYGBIV color assignment

  // Load sprite texture for beautiful circular sprites
  let spriteTexture: any = null;
  loadSpriteTexture(THREE).then((texture) => {
    spriteTexture = texture;
  });

  /**
   * Create a green network request line
   */
  function createNetworkRequestLine(isStartup?: boolean): void {
    // Don't create if we're at max - let lines die naturally instead of removing oldest
    if (networkRequestLines.length >= config.MAX_NETWORK_LINES) {
      return;
    }

    // Get camera position (user's avatar location)
    let cameraPosition = { x: 0, y: 0, z: 50 };
    if (camera) {
      cameraPosition = {
        x: (camera as any).position.x,
        y: (camera as any).position.y,
        z: (camera as any).position.z,
      };
    }

    const currentTime = performance.now() * 0.001;
    const duration =
      config.NETWORK_LINE_DURATION_MIN +
      Math.random() * (config.NETWORK_LINE_DURATION_MAX - config.NETWORK_LINE_DURATION_MIN);

    // Network responses: incoming from cortex to camera
    const path = generateConnectionPath('network', pathOptions);

    if (path.length === 0) {
      return;
    }

    // Create geometry with path points (already in world space)
    const geometry = new BufferGeometry();
    const positions = new Float32Array(path.length * 3);
    for (let i = 0; i < path.length; i++) {
      positions[i * 3] = path[i].x;
      positions[i * 3 + 1] = path[i].y;
      positions[i * 3 + 2] = path[i].z;
    }
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));

    // Create green material - will fade from transparent to brighter
    const material = new LineBasicMaterial({
      color: 0x00ff00, // Bright green
      opacity: 0.12, // Start very transparent - will fade to brighter at end
      transparent: true,
      linewidth: 4, // Much thicker - 4 pixels
    });

    const line = new Line(geometry, material);
    scene.add(line);

    const from = path[0];
    const to = path[path.length - 1];

    // Special colors for startup: Red, Yellow, Green, Blue (only a few, rest are white)
    const startupColors: { [key: number]: number } = {
      0: 0,    // Red
      1: 0.17, // Yellow
      2: 0.33, // Green
      3: 0.67, // Blue
    };
    let colorHueOffset: number;
    if (isStartup) {
      // Only assign colors to first 4 particles, rest are white (default blue-green)
      if (startupColors.hasOwnProperty(startupLineIndex)) {
        colorHueOffset = startupColors[startupLineIndex];
      } else {
        // White/default color for the rest
        colorHueOffset = 0.45 + Math.random() * 0.1; // Blue-green range (appears white/cyan)
      }
      startupLineIndex++;
    } else {
      // Slight variation in blue-green range (cyan is ~0.5, so vary from 0.45 to 0.55)
      colorHueOffset = 0.45 + Math.random() * 0.1; // Blue-green range only
    }

    const networkLine: NetworkRequestLine = {
      from,
      to,
      progress: 0,
      duration,
      startTime: currentTime,
      line,
      sprite: null, // Will be created below
      path,
      colorHueOffset,
      status: 'normal', // Default to normal, can be set to 'error' or 'warning'
      isStartup: isStartup || false,
    };

    networkRequestLines.push(networkLine);

    // Create sprite at the end of the line to make it look like a trail
    // Use beautiful circular sprite texture if available
    // Color will be varied, rotated, and oscillated per sprite
    const spriteMaterial = new SpriteMaterial({
      color: 0x00ffff, // Initial color (will be updated dynamically)
      opacity: 1.0, // Brighter - full opacity
      transparent: true,
      sizeAttenuation: true,
      map: spriteTexture || null, // Use generated sprite texture
      blending: spriteTexture ? (THREE as any).AdditiveBlending : (THREE as any).NormalBlending, // Glowing effect if texture loaded
      depthWrite: false,
    });
    const sprite = new Sprite(spriteMaterial);
    sprite.scale.set(3, 3, 1); // Larger size for visibility - increased from 2
    sprite.position.set(0, 0, 0); // Will be updated each frame
    networkLine.sprite = sprite;

    scene.add(sprite);
  }

  /**
   * Update network request lines
   */
  function updateNetworkRequestLines(currentTime: number): void {
    for (let i = networkRequestLines.length - 1; i >= 0; i--) {
      const networkLine = networkRequestLines[i];

      // Calculate progress
      const elapsed = currentTime - networkLine.startTime;
      networkLine.progress = Math.min(1, Math.max(0, elapsed / networkLine.duration));

      // Remove line immediately when it reaches the end (progress >= 1)
      if (networkLine.progress >= 1) {
        // Remove line
        if (networkLine.line) {
          scene.remove(networkLine.line);
          networkLine.line.geometry.dispose();
          (networkLine.line.material as any).dispose();
        }
        // Remove sprite
        if (networkLine.sprite) {
          scene.remove(networkLine.sprite);
          (networkLine.sprite.material as any).dispose();
        }
        networkRequestLines.splice(i, 1);
        continue;
      }

      // Update line to show progress along the arcing path with trailing effect
      const geometry = networkLine.line.geometry;
      const positions = geometry.attributes.position.array as Float32Array;
      const path = networkLine.path || [];
      // Paths are already in world space (no conversion needed)

      if (path.length > 0) {
        // Calculate current end position along the path (in world space)
        const totalPathLength = path.length - 1;
        const endPathProgress = networkLine.progress * totalPathLength;
        const endSegmentIndex = Math.floor(endPathProgress);
        const endSegmentProgress = endPathProgress - endSegmentIndex;

        const endSegmentStart = path[Math.min(endSegmentIndex, path.length - 1)];
        const endSegmentEnd = path[Math.min(endSegmentIndex + 1, path.length - 1)];

        let currentEndX =
          endSegmentStart.x + (endSegmentEnd.x - endSegmentStart.x) * endSegmentProgress;
        let currentEndY =
          endSegmentStart.y + (endSegmentEnd.y - endSegmentStart.y) * endSegmentProgress;
        let currentEndZ =
          endSegmentStart.z + (endSegmentEnd.z - endSegmentStart.z) * endSegmentProgress;

        // Extend line by 10% beyond the last path point to ensure it touches the cubes
        if (endSegmentIndex >= path.length - 2) {
          const lastPoint = path[path.length - 1];
          const secondLastPoint = path[path.length - 2] || lastPoint;
          const directionX = lastPoint.x - secondLastPoint.x;
          const directionY = lastPoint.y - secondLastPoint.y;
          const directionZ = lastPoint.z - secondLastPoint.z;
          const extensionFactor = 0.10; // 10% extension
          currentEndX = lastPoint.x + directionX * extensionFactor;
          currentEndY = lastPoint.y + directionY * extensionFactor;
          currentEndZ = lastPoint.z + directionZ * extensionFactor;
        }

        // Calculate start position (always starts at beginning, no shrink phase)
        const startPathProgress = 0;
        const startSegmentIndex = 0;
        const startSegmentProgress = 0;

        const startSegmentStart = path[Math.min(startSegmentIndex, path.length - 1)];
        const startSegmentEnd = path[Math.min(startSegmentIndex + 1, path.length - 1)];

        const currentStartX =
          startSegmentStart.x + (startSegmentEnd.x - startSegmentStart.x) * startSegmentProgress;
        const currentStartY =
          startSegmentStart.y + (startSegmentEnd.y - startSegmentStart.y) * startSegmentProgress;
        const currentStartZ =
          startSegmentStart.z + (startSegmentEnd.z - startSegmentStart.z) * startSegmentProgress;

        // Build the line from current start to current end
        // Limit line length to avoid "ponytail" effect - only show last N segments
        const maxLineSegments = 3; // Maximum number of segments to show
        const effectiveStartSegment = Math.max(startSegmentIndex, endSegmentIndex - maxLineSegments);

        let pointIndex = 0;

        // Add start position (or effective start if limited)
        const effectiveStartSegmentStart = path[Math.min(effectiveStartSegment, path.length - 1)];
        const effectiveStartSegmentEnd = path[Math.min(effectiveStartSegment + 1, path.length - 1)];
        const effectiveStartT =
          effectiveStartSegment === startSegmentIndex ? startSegmentProgress : 0;
        const effectiveStartX =
          effectiveStartSegmentStart.x +
          (effectiveStartSegmentEnd.x - effectiveStartSegmentStart.x) * effectiveStartT;
        const effectiveStartY =
          effectiveStartSegmentStart.y +
          (effectiveStartSegmentEnd.y - effectiveStartSegmentStart.y) * effectiveStartT;
        const effectiveStartZ =
          effectiveStartSegmentStart.z +
          (effectiveStartSegmentEnd.z - effectiveStartSegmentStart.z) * effectiveStartT;

        // Add start position (paths are in world space)
        positions[pointIndex * 3] = effectiveStartX;
        positions[pointIndex * 3 + 1] = effectiveStartY;
        positions[pointIndex * 3 + 2] = effectiveStartZ;
        pointIndex++;

        // Add intermediate path points between effective start and end segments (limited)
        for (let seg = effectiveStartSegment + 1; seg < endSegmentIndex; seg++) {
          if (seg < path.length) {
            positions[pointIndex * 3] = path[seg].x;
            positions[pointIndex * 3 + 1] = path[seg].y;
            positions[pointIndex * 3 + 2] = path[seg].z;
            pointIndex++;
          }
        }

        // Add end position
        positions[pointIndex * 3] = currentEndX;
        positions[pointIndex * 3 + 1] = currentEndY;
        positions[pointIndex * 3 + 2] = currentEndZ;
        pointIndex++;

        geometry.setDrawRange(0, pointIndex);

        // Update sprite position to track the end of the line
        if (networkLine.sprite) {
          networkLine.sprite.position.set(currentEndX, currentEndY, currentEndZ);
          
          // Update sprite color based on status
          let hue: number;
          let saturation: number;
          let lightness: number;
          
          if (networkLine.status === 'error') {
            // Red for errors
            hue = 0; // Red
            saturation = 0.9;
            lightness = 0.5;
          } else if (networkLine.status === 'warning') {
            // Orange/yellow for warnings
            hue = 0.1; // Orange-yellow
            saturation = 0.9;
            lightness = 0.5;
          } else {
            // Blue-green range for normal connections (slight variation per sprite)
            hue = networkLine.colorHueOffset; // Already in blue-green range (0.45-0.55)
            saturation = 0.8;
            lightness = 0.5;
          }
          
          // Convert HSL to RGB
          const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
          const x = c * (1 - Math.abs((hue * 6) % 2 - 1));
          const m = lightness - c / 2;
          let r = 0, g = 0, b = 0;
          
          if (hue < 1/6) { r = c; g = x; b = 0; }
          else if (hue < 2/6) { r = x; g = c; b = 0; }
          else if (hue < 3/6) { r = 0; g = c; b = x; }
          else if (hue < 4/6) { r = 0; g = x; b = c; }
          else if (hue < 5/6) { r = x; g = 0; b = c; }
          else { r = c; g = 0; b = x; }
          
          const color = Math.floor((r + m) * 255) << 16 | Math.floor((g + m) * 255) << 8 | Math.floor((b + m) * 255);
          (networkLine.sprite.material as any).color.setHex(color);
        }

        // Calculate opacity for tail effect - fade from transparent at start to brighter at end
        // Use progress along the visible line segment to create gradient
        const totalVisiblePoints = pointIndex;
        if (totalVisiblePoints > 1) {
          // Calculate opacity gradient: transparent at start, brighter at end (where sprite is)
          // Start opacity is very low (0.03), end opacity is higher (0.35)
          const startOpacity = 0.03;
          const endOpacity = 0.35;
          // Average opacity across the line for material
          const avgOpacity = (startOpacity + endOpacity) / 2;
          (networkLine.line.material as any).opacity = Math.max(0, avgOpacity);
        } else {
          (networkLine.line.material as any).opacity = 0.12; // Fallback
        }
        (networkLine.line.material as any).transparent = true;
        (networkLine.line.material as any).needsUpdate = true;
      }

      geometry.attributes.position.needsUpdate = true;
      geometry.computeBoundingSphere();
    }
  }

  // Listen for network request events - create green line directly (event-driven)
  bus.on('network.request.started', (payload: any) => {
    // Check if this is a startup event (privacy activation)
    const isStartup = payload?.requestId?.includes('privacy-activation') || payload?._test === true;
    
    // Create green line directly from event (event-driven, like white lines)
    // Only create if we're below max lines to prevent excessive accumulation
    if (networkRequestLines.length < config.MAX_NETWORK_LINES) {
      createNetworkRequestLine(isStartup);
    }
  });

  return {
    networkRequestLines,
    createNetworkRequestLine: (isStartup?: boolean) => {
      createNetworkRequestLine(isStartup);
    },
    updateNetworkRequestLines,
  };
}

